import { useState, useEffect } from 'react';
import { client } from '@/api/client';
import { Printer, FileDown } from 'lucide-react';
import DateRangeFilter from './DateRangeFilter';
import { exportCSV, inRange } from './reportUtils';
import { useCompanyInfo, generatePrintHTML, openPrint } from './ReportExporter';

const EXPENSE_CATEGORIES = ['Fuel', 'Toll', 'Scale', 'Maintenance', 'Tire', 'Other'];
const IRS_1099_THRESHOLD = 600;

// Extends the same P&L math (Gross Revenue - Expenses - Driver Pay - Carrier
// Take = Net) with a deductible-expense breakdown by category and a 1099-NEC
// contractor payment summary for Owner Operators, flagging anyone at or
// above the IRS $600 reporting threshold for the period.
export default function TaxReport() {
  const [loads, setLoads] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [driverProfiles, setDriverProfiles] = useState([]);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [loading, setLoading] = useState(true);
  const company = useCompanyInfo();

  useEffect(() => {
    Promise.all([
      client.entities.Load.list('-delivery_date', 1000),
      client.entities.Expense.list('-date', 1000),
      client.entities.Settlement.list('-settlement_date', 1000),
      client.entities.DriverProfile.list('full_name', 200),
    ]).then(([l, e, s, d]) => { setLoads(l); setExpenses(e); setSettlements(s); setDriverProfiles(d); setLoading(false); });
  }, []);

  const filteredLoads = loads.filter(l => inRange(l.delivery_date || l.pickup_date, start, end) && l.status !== 'Available');
  const filteredExpenses = expenses.filter(e => inRange(e.date, start, end));
  const filteredSettlements = settlements.filter(s => inRange(s.settlement_date || s.finalized_date, start, end) && s.is_finalized);

  const grossRevenue = filteredLoads.reduce((s, l) => s + (l.rate || 0), 0);
  const carrierTake = filteredSettlements.reduce((s, s2) => s + (s2.carrier_take_amount || 0) + (s2.dispatch_fee_amount || 0), 0);
  const driverPay = filteredSettlements.reduce((s, s2) => s + (s2.final_net_pay ?? s2.net_driver_pay ?? 0), 0);
  const totalExpenses = filteredExpenses.reduce((s, e) => s + (e.amount || 0), 0);

  const byCategory = EXPENSE_CATEGORIES.reduce((acc, cat) => {
    const amt = filteredExpenses.filter(e => e.category === cat).reduce((s, e) => s + (e.amount || 0), 0);
    if (amt > 0) acc[cat] = amt;
    return acc;
  }, {});

  const ownerOpEmails = new Set(driverProfiles.filter(d => d.driver_type === 'Owner Operator').map(d => d.user_email));
  const byContractor = {};
  filteredSettlements.forEach(s => {
    if (!ownerOpEmails.has(s.driver_email)) return;
    const key = s.driver_email;
    if (!byContractor[key]) byContractor[key] = { name: s.driver_name, total: 0 };
    byContractor[key].total += (s.final_net_pay ?? s.net_driver_pay ?? 0);
  });
  const contractorRows = Object.values(byContractor).sort((a, b) => b.total - a.total);

  const csvRows = [
    { Metric: 'Gross Revenue', Amount: grossRevenue.toFixed(2) },
    ...Object.entries(byCategory).map(([cat, amt]) => ({ Metric: `Expense: ${cat}`, Amount: (-amt).toFixed(2) })),
    { Metric: 'Driver/Carrier Pay', Amount: (-driverPay).toFixed(2) },
    { Metric: 'Carrier Take / Dispatch Fees', Amount: (-carrierTake).toFixed(2) },
    { Metric: 'Net Taxable Income (est.)', Amount: (grossRevenue - totalExpenses - driverPay - carrierTake).toFixed(2) },
  ];

  const handlePrint = () => {
    const cols = ['Line Item', 'Amount'];
    const dataRows = [
      [{ value: 'Gross Revenue', class: 'bold' }, { value: `$${grossRevenue.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, class: 'positive' }],
      ...Object.entries(byCategory).map(([cat, amt]) => [`Expense — ${cat}`, { value: `-$${amt.toFixed(2)}`, class: 'negative' }]),
      ['Driver / Carrier Pay', { value: `-$${driverPay.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, class: 'negative' }],
      ['Carrier Take / Dispatch Fees', { value: `-$${carrierTake.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, class: 'negative' }],
    ];
    const netEst = grossRevenue - totalExpenses - driverPay - carrierTake;
    const totalsRow = ['Net Taxable Income (estimate)', { value: `$${netEst.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, class: netEst >= 0 ? 'positive' : 'negative' }];
    const cards = [
      { label: 'Gross Revenue', value: `$${grossRevenue.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, color: 'positive' },
      { label: 'Deductible Expenses', value: `$${totalExpenses.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, color: 'negative' },
      { label: 'Net Taxable Income', value: `$${netEst.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, color: netEst >= 0 ? 'positive' : 'negative' },
      { label: '1099 Contractors', value: contractorRows.filter(c => c.total >= IRS_1099_THRESHOLD).length, color: 'gold' },
    ];
    const html = generatePrintHTML('Tax Summary Report', `${start || 'All'} – ${end || 'All'}`, cols, dataRows, totalsRow, company, cards);
    openPrint(html);
  };

  const netEst = grossRevenue - totalExpenses - driverPay - carrierTake;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <DateRangeFilter onChange={(s, e) => { setStart(s); setEnd(e); }} onExport={() => exportCSV(csvRows, 'tax-report.csv')} exportDisabled={loading} />
        <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-border text-sm text-foreground hover:bg-white/10"><Printer className="w-4 h-4" /> Print</button>
        <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-border text-sm text-foreground hover:bg-white/10"><FileDown className="w-4 h-4" /> Export PDF</button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Gross Revenue', value: `$${grossRevenue.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, color: 'text-green-400' },
          { label: 'Deductible Expenses', value: `$${totalExpenses.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, color: 'text-red-400' },
          { label: 'Net Taxable Income', value: `$${netEst.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, color: netEst >= 0 ? 'text-green-400' : 'text-red-400' },
          { label: '1099s Required', value: contractorRows.filter(c => c.total >= IRS_1099_THRESHOLD).length, color: 'text-primary' },
        ].map(i => (
          <div key={i.label} className="glass-card p-4 text-center">
            <p className={`text-lg font-space font-bold ${i.color}`}>{i.value}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{i.label}</p>
          </div>
        ))}
      </div>

      <div className="glass-card p-4">
        <p className="text-xs font-semibold text-muted-foreground mb-3 uppercase tracking-widest">Deductible Expenses by Category</p>
        {Object.keys(byCategory).length === 0 ? (
          <p className="text-xs text-muted-foreground">No expenses logged for this period.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {Object.entries(byCategory).map(([cat, amt]) => (
              <div key={cat} className="flex items-center justify-between px-3 py-2 rounded-lg bg-muted/40 border border-border">
                <span className="text-xs text-foreground">{cat}</span>
                <span className="text-xs font-bold text-red-400">${amt.toFixed(2)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="glass-card overflow-x-auto">
        <div className="px-4 pt-4 pb-1 flex items-center justify-between">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">1099-NEC Contractor Payments (Owner Operators)</p>
          <p className="text-xs text-muted-foreground">IRS threshold: ${IRS_1099_THRESHOLD}</p>
        </div>
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border">
              {['Contractor', 'Total Paid', '1099 Required'].map(h => (
                <th key={h} className="text-left px-3 py-2.5 text-muted-foreground font-medium">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {contractorRows.map(c => (
              <tr key={c.name} className="hover:bg-white/3">
                <td className="px-3 py-2.5 font-semibold text-foreground">{c.name}</td>
                <td className="px-3 py-2.5 text-foreground">${c.total.toFixed(2)}</td>
                <td className="px-3 py-2.5">
                  {c.total >= IRS_1099_THRESHOLD
                    ? <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border text-amber-700 dark:text-amber-400 bg-amber-500/15 dark:bg-amber-500/10 border-amber-500/40 dark:border-amber-500/30">Yes</span>
                    : <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border text-muted-foreground bg-muted/40 border-border">No</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {contractorRows.length === 0 && !loading && <p className="text-center text-muted-foreground py-8 text-sm">No owner operator payments for this period.</p>}
      </div>
      <p className="text-xs text-muted-foreground">Estimate only — consult your accountant before filing. Net Taxable Income = Gross Revenue − logged Expenses − finalized Driver/Carrier settlement pay. Does not account for depreciation, per-diem, or other tax-specific adjustments.</p>
    </div>
  );
}
